/**
 * Objectives - Displays current mission objectives
 */

import React from 'react';
import { useSelector } from 'react-redux';
import type { RootState } from '../../state/store';

interface ObjectivesProps {
  position?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  collapsed?: boolean;
}

export const Objectives: React.FC<ObjectivesProps> = ({
  position = 'top-left',
  collapsed = false
}) => {
  const [isCollapsed, setIsCollapsed] = React.useState(collapsed);

  // Get objectives from Redux state
  const objectives = useSelector((state: RootState) =>
    state.game.objectives.filter(obj => !obj.hidden)
  );

  const positionStyles = {
    'top-left': { top: '150px', left: '20px' },
    'top-right': { top: '150px', right: '20px' },
    'bottom-left': { bottom: '80px', left: '20px' },
    'bottom-right': { bottom: '80px', right: '20px' },
  };

  const completedCount = objectives.filter(obj => obj.completed).length;
  const totalCount = objectives.filter(obj => obj.required).length;

  // Don't render if no objectives
  if (objectives.length === 0) {
    return null;
  }

  return (
    <div style={{
      position: 'absolute',
      ...positionStyles[position],
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      fontFamily: 'monospace',
      color: '#ffffff',
      pointerEvents: 'auto',
      userSelect: 'none',
      minWidth: '250px',
    }}>
      {/* Header */}
      <div
        onClick={() => setIsCollapsed(!isCollapsed)}
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '8px 12px',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          border: '2px solid #ffaa00',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 'bold',
        }}
      >
        <span>OBJECTIVES ({completedCount}/{totalCount})</span>
        <span style={{ fontSize: '12px' }}>{isCollapsed ? '▼' : '▲'}</span>
      </div>

      {/* Objectives list */}
      {!isCollapsed && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '6px',
          padding: '12px',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          border: '2px solid #444',
          borderRadius: '4px',
        }}>
          {objectives.map((objective) => (
            <div
              key={objective.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                fontSize: '12px',
                opacity: objective.completed ? 0.6 : 1,
              }}
            >
              {/* Checkbox */}
              <div style={{
                width: '16px',
                height: '16px',
                border: `2px solid ${objective.completed ? '#00ff00' : '#888'}`,
                borderRadius: '3px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: objective.completed ? '#00ff00' : 'transparent',
                flexShrink: 0,
              }}>
                {objective.completed && (
                  <span style={{ color: '#000', fontSize: '12px', fontWeight: 'bold' }}>✓</span>
                )}
              </div>

              {/* Description */}
              <span style={{
                textDecoration: objective.completed ? 'line-through' : 'none',
                color: !objective.required ? '#aaaaaa' : '#ffffff',
              }}>
                {objective.title}
                {!objective.required && (
                  <span style={{ fontSize: '10px', marginLeft: '6px', color: '#888' }}>
                    (Optional)
                  </span>
                )}
                {/* Show progress if applicable */}
                {objective.targetValue !== undefined && objective.currentValue !== undefined && (
                  <span style={{ fontSize: '10px', marginLeft: '6px', color: '#ffaa00' }}>
                    ({objective.currentValue}/{objective.targetValue})
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
